import { toast } from 'sonner'

import { directionFor } from '@/lib/i18n/direction'
import { sourceTypeLabel } from '@/lib/recipients/staging-labels'

/**
 * Switching a person off says when a directory sync will switch them back on
 * (Recipient status report, issue 1, 26 Oct 2026). Not "the next sync": an
 * HFv2 sync sends only what changed, so it may be a later one.
 *
 * Every sync writes the directory's status again unless its connection
 * protects Status, so a person switched off by hand came back Active and nobody
 * was told. The API answers a switch-off (PUT /recipients/:id, POST
 * /recipients/bulk-status) with `syncWillReactivate: { count, sources }` when
 * that will happen, computed with the sync's own rule; the screens only show
 * what it says.
 */
export interface SyncWillReactivate {
  count: number
  /** Source types as the API names them ('Okta', 'Microsoft O365', 'AD Syncer', …). */
  sources: string[]
}

type Translate = (key: string, params?: Record<string, string | number>) => string

/** The note the API attached to a switch-off answer, or null when there is nothing to say. */
export function syncWillReactivateOf(answer: unknown): SyncWillReactivate | null {
  if (!answer || typeof answer !== 'object') return null
  const raw = (answer as { syncWillReactivate?: unknown }).syncWillReactivate
  if (!raw || typeof raw !== 'object') return null
  const { count, sources } = raw as { count?: unknown; sources?: unknown }
  if (typeof count !== 'number' || count <= 0) return null
  if (!Array.isArray(sources) || sources.length === 0 || !sources.every((s) => typeof s === 'string')) return null
  return { count, sources: sources as string[] }
}

/**
 * "Okta and LDAP", in the interface language; a plain list where
 * Intl.ListFormat is missing. On a right-to-left screen each name is isolated
 * (FSI…PDI) first: a Latin name leading the list ('Okta و دليل LDAP/AD') made
 * the whole list run left to right and scrambled the Arabic between the names.
 */
function joinNames(names: string[], locale: string): string {
  const shown = directionFor(locale) === 'rtl' ? names.map((n) => `⁨${n}⁩`) : names
  try {
    return new Intl.ListFormat(locale || 'en', { style: 'long', type: 'conjunction' }).format(shown)
  } catch {
    return shown.join(', ')
  }
}

/**
 * The amber note, after the save. It never throws: a throw in a mutation's
 * onSuccess would make TanStack report a SAVED change as failed, and one in the
 * bulk handler's `.then` would undo it.
 */
export function toastSyncWillReactivate(t: Translate, locale: string, note: SyncWillReactivate): void {
  try {
    const sources = joinNames(note.sources.map((s) => sourceTypeLabel(t, s) || s), locale)
    toast.warning(t('recipients.syncWillReactivate.title', { count: note.count }), {
      id: 'recipients-sync-will-reactivate',
      description: t('recipients.syncWillReactivate.description', { count: note.count, sources }),
      duration: 10_000,
    })
  } catch {
    // A note that cannot be shown changes nothing that was saved.
  }
}
